import React, { useState } from 'react';
import { Text, View } from 'react-native';
import { StyledModal } from '../StyledModal';
import ErrorBoundary from '../errorBoundaries/ErrorBoundary';
import { StateProgramSubmission } from './StateProgramSubmission';
import { useLocalization } from '../../store/utils/useLocalization';
import { useStateProgramContext } from './StateProgramContext';
import { StateProgramFormFrame } from './StateProgramFormFrame';

interface StateProgramWizardProps {
  isVisible: boolean;
  onClose: () => void;
  userId: number;
  visitId: number;
}

export function StateProgramWizard(props: StateProgramWizardProps): React.ReactNode {
  const { t } = useLocalization();
  const programServices = useStateProgramContext();
  const [hasStartedSubmission, setHasStartedSubmission] = useState(false);
  const [hasRuntimeError, setHasRuntimeError] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);

  const startSubmission = () => {
    // just flag it and let the submission component handle the rest
    setHasStartedSubmission(true);
  };

  const closeWizard = () => {
    // clear submission and error state before closing so it can retry
    setHasStartedSubmission(false);
    setHasRuntimeError(false);
    props.onClose();
  };

  const formUpdated = () => {
    setHasValidationError(!programServices.frameUpdateStatus?.success);
  };

  return (
    <StyledModal
      testID={`StateProgramWizard-${programServices.id}`}
      headerText={programServices.name}
      isVisible={props.isVisible}
      onClose={closeWizard}
      buttons={[
        {
          testID: `StateProgramWizard-${programServices.id}-closeButton`,
          onPress: closeWizard,
          localeKey: 'app_button_close',
        },
        // TODO #3065: Wait until final frame before submitting
        // (until frames are built we'll submit as soon as neighbor clicks to complete,
        // as if it's an opt-in only attestation)
        ...(!hasStartedSubmission && !hasRuntimeError
          ? [
              {
                testID: `StateProgramWizard-${programServices.id}-submitButton`,
                onPress: startSubmission,
                localeKey: 'app_button_submit',
                filled: true,
                disabled: hasValidationError,
              },
            ]
          : []),
      ]}
    >
      <ErrorBoundary
        fallback={
          <Text>
            {
              // Show the retry/support message if ANY error happens once we've already started the attestation
              t('state_program_attestation_cannot_complete', { programName: programServices.name })
            }
          </Text>
        }
        onError={() => setHasRuntimeError(true)}
      >
        <View>
          {!hasStartedSubmission ? (
            <StateProgramFormFrame onChange={formUpdated} />
          ) : (
            <StateProgramSubmission
              userId={props.userId}
              visitId={props.visitId}
              isComplete={
                // TODO: #3051: allow for submitting partial attestations
                true
              }
            />
          )}
        </View>
      </ErrorBoundary>
    </StyledModal>
  );
}
